import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Row } from 'reactstrap';
import { defineMessages, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faChevronLeft,
} from '@fortawesome/free-solid-svg-icons';
import {
  faChevronsLeft,
  faChevronsRight,
} from '@fortawesome/pro-solid-svg-icons';

import Canonical from 'Components/Utility/SEO/Canonical';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import useViewport from 'Hooks/useViewport';

const messages = defineMessages({
  page: {
    id: 'algolia-paginate.page',
    defaultMessage: 'Page {pageNumber}',
  },
  first: {
    id: 'algolia-paginate.first',
    defaultMessage: 'First Page',
  },
  last: {
    id: 'algolia-paginate.last',
    defaultMessage: 'Last Page',
  },
  next: {
    id: 'algolia-paginate.next',
    defaultMessage: 'Next Page',
  },
  prev: {
    id: 'algolia-paginate.prev',
    defaultMessage: 'Previous Page',
  },
});


/**
 * Page is now controlled by the url parameter and is passed to the updateHits function prop
 * to actually fetch the hits
 */
function Paginate({
  page,
  hits,
  pageLimit,
  updateHits,
  pathname,
  search,
  hideFirstLast,
  hideNextPrev,
  hash,
}) {
  const intl = useIntl();

  const { isAboveXs } = useViewport();

  useEffect(() => {
    if ((page - 1) !== hits.page) {
      updateHits(page);
    }
  }, [page, hits.page, updateHits]);

  if ((page - 1) !== hits.page) {
    return false;
  }

  const isFirst = page === 1;
  const isSecond = page === 2;
  const totalPages = Math.min(hits.nbPages, pageLimit);
  const isPenultimate = page === (totalPages - 1);
  const isLast = page === totalPages;

  if (totalPages < 2) {
    return false;
  }

  const button = (pageNumber, contents, label, icon) => {
    const nextPages = isFirst ? 4 : isSecond ? 3 : 2; // eslint-disable-line no-nested-ternary
    const prevPages = isLast ? 4 : isPenultimate ? 3 : 2; // eslint-disable-line no-nested-ternary

    if (pageNumber < 1 || pageNumber > totalPages) {
      return false;
    }

    const hidden = !label && (page > (pageNumber + prevPages) || page < (pageNumber - nextPages));

    if (hidden) {
      return null;
    }

    let path = `${pathname}${search}`;

    if (!isFirst) {
      if (pageNumber === 1) {
        path = path.replace(/page=(\d+)/, '').replace(/[&?]$/, '');
      }
      path = path.replace(`page=${page}`, `page=${pageNumber}`);
    } else if (pageNumber > 1) {
      const hasQueryParam = !!search;
      path = `${path}${hasQueryParam ? '&' : '?'}page=${pageNumber}`;
    }

    if (hash) {
      path = `${path}#${hash}`;
    }

    return (
      <li key={`${pageNumber}${label || ''}`}>
        <Link
          to={path}
          className={classNames(
            'page-item',
            { hidden },
          )}
          aria-label={label || intl.formatMessage(messages.page, { pageNumber })}
          title={label || intl.formatMessage(messages.page, { pageNumber })}
        >
          <button className={classNames('page-link', { active: pageNumber === page })}>
            {!icon && pageNumber}
            {icon && contents === '\u00ab' &&
              <FontAwesomeIcon icon={faChevronsLeft} title="First" />
            }
            {icon && contents === '\u2039' &&
              <FontAwesomeIcon icon={faChevronLeft} title="Previous" />
            }
            {icon && contents === '\u203a' &&
              <FontAwesomeIcon icon={faChevronRight} title="Next" />
            }
            {icon && contents === '\u00bb' &&
              <FontAwesomeIcon icon={faChevronsRight} title="Last" />
            }
          </button>
        </Link>
      </li>
    );
  };

  const numbers = [...Array(totalPages).keys()].map(i => i + 1);

  return (
    <div className="container">
      <Canonical
        page={page - 1}
        nbPages={totalPages}
        path={pathname.replace(/^\//, '')}
      />
      <Row className="d-flex justify-content-center algolia-paginate">
        <nav aria-label="Page navigation" className="algoliaPaginate">
          <ul className="pagination">
            {isAboveXs && !hideFirstLast && !isFirst && button(1, '\u00ab', intl.formatMessage(messages.first), true)}
            {!hideNextPrev && !isFirst && button(page - 1, '\u2039', intl.formatMessage(messages.prev), true)}
            {numbers.map(pageNumber => button(pageNumber))}
            {!hideNextPrev && !isLast && button(page + 1, '\u203a', intl.formatMessage(messages.next), true)}
            {isAboveXs && !hideFirstLast && !isLast && button(totalPages, '\u00bb', intl.formatMessage(messages.last), true)}
          </ul>
        </nav>
      </Row>
    </div>
  );
}

Paginate.propTypes = {
  updateHits: PropTypes.func,
  pathname: PropTypes.string.isRequired,
  search: PropTypes.string.isRequired,
  hash: PropTypes.string,
  page: PropTypes.number,
  pageLimit: PropTypes.number,
  hits: PropTypes.shape({
    nbPages: PropTypes.number,
    page: PropTypes.number,
  }),
  hideFirstLast: PropTypes.bool,
  hideNextPrev: PropTypes.bool,
};

Paginate.defaultProps = {
  updateHits: () => {},
  page: 1,
  pageLimit: 500,
  hash: '',
  hits: {
    nbPages: 1,
    page: 1,
  },
  hideFirstLast: false,
  hideNextPrev: false,
};

export default connect(({
  router: { location: { href, pathname, search } },
}) => ({
  pathname,
  search,
  page: Number(getUrlParameter('page', href) || 1),
}))(Paginate);
